import React from 'react';
import classNames from 'classnames';
import { SectionHeading } from '~/components/molecules/heading/SectionHeading';
import IndicesFilter from '~/components/molecules/indicesFilters/IndicesFilter';
import NoResults from '~/components/molecules/noResults/noResults';
import Pager from '~/components/molecules/pager/pager';
import { useSERPState } from '~/core/state/SERPStateContext';
import { QuerySet_ControlsState } from '~/core/state/types/state-controls.type';
import { FulltextResults_State } from '~/core/state/types/state-results.schema';
import { QuerySet_State } from '~/core/state/types/state-sets.type';
import { UpdateIndexFilterControls } from '~/core/useSearchState';
import { GoToResultHandler } from '~/hooks/useGoToResult';
import { useSelectedTab } from '~/hooks/useSelectedTab';
import { useShouldRenderSet } from '~/hooks/useShouldRenderSet';
import { DynamicComponents } from '~/plugins/dynamicComponents/dynamicComponents';
import { useDynamicComponents } from '~/plugins/dynamicComponents/dynamicComponentsContext';
import styles from './fulltext.module.scss';

export interface IFullTextProps {
    set: QuerySet_State;
    controls: QuerySet_ControlsState;
    results: FulltextResults_State;
    loadMore: () => void;
    updateIndexFilterControls: UpdateIndexFilterControls;
    showLoadMore: boolean;
    isInsideProducts: boolean;
    title:
        | string
        | {
              id: string;
              defaultMessage: string;
          };
    hasIcons: boolean;
    handleGoToResult: GoToResultHandler;
    isFulltextOnly: boolean;
    renderProps: {
        FulltextResultHeading: DynamicComponents['FulltextResultHeading'];
        FulltextResultWrapper: DynamicComponents['FulltextResultWrapper'];
    };
}

const FullText: React.FC<IFullTextProps> = ({
    set,
    controls,
    results,
    loadMore,
    updateIndexFilterControls,
    showLoadMore,
    isInsideProducts,
    title,
    hasIcons,
    handleGoToResult,
    isFulltextOnly,
    renderProps,
}) => {
    const showIndicesFilter =
        set.indices &&
        set.indices.length > 0 &&
        set.setting &&
        !Array.isArray(set.setting) &&
        set.setting.indexFiltering;

    const { FulltextSingleResult } = useDynamicComponents();
    const state = useSERPState();
    const { activeTab } = useSelectedTab();
    const shouldRenderOnMobile = useShouldRenderSet({ identifier: set.querySet.identifier });

    if (!shouldRenderOnMobile) {
        return null;
    }

    return (
        <div
            className={classNames(styles.fulltext, 'serp-fulltext', {
                [styles.withRows]: state.resultLayout === 'rows',
            })}
            data-testid="fulltext-results"
            id={`tabpanel-${activeTab?.title}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab?.title}`}
        >
            {!isFulltextOnly ? <SectionHeading text={title} data-testid="fulltext-title" /> : null}
            {showIndicesFilter && set.indices ? (
                <IndicesFilter
                    indicesState={set.indices}
                    indicesControls={controls.indices ?? []}
                    querySetIdentifier={set.querySet.identifier}
                    updateIndexFilterControls={updateIndexFilterControls}
                />
            ) : null}
            {results.hits.length > 0 ? (
                <div
                    className={classNames(styles.fulltext, {
                        [styles.fulltextInsideProducts]: isInsideProducts,
                        [styles.withRows]: state.resultLayout === 'rows',
                    })}
                >
                    <div className="serp-fulltext-results-container" data-testid="fulltext-results-container">
                        {results.hits.map((result, index) => {
                            return (
                                <FulltextSingleResult
                                    result={result}
                                    key={index}
                                    hasIcons={hasIcons}
                                    order={index + 1}
                                    handleGoToResult={handleGoToResult}
                                    renderProps={renderProps}
                                />
                            );
                        })}
                    </div>
                    {showLoadMore ? <Pager loadMore={loadMore} /> : null}
                </div>
            ) : (
                <NoResults />
            )}
        </div>
    );
};

export default FullText;
